/**
 * Custom hook for offer file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchOfferFiles,
  type OfferFileSearchFilters,
  type OfferFileSearchResponse,
} from '../api/offerFileSearchApi'

/**
 * Hook for searching offer files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with offer file data
 */
export function useOfferFileSearch(
  filters: OfferFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: OfferFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<OfferFileSearchResponse, Error>({
    queryKey: ['offer-file-search', safeFilters],
    queryFn: () => searchOfferFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { OfferFileSearchFilters, OfferFileSearchResponse }
